import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { VacantesService, SugerenciaUbicacion } from '../../core/services/vacantes.service';
import { CategoriaService } from '../../core/services/categoria.service';
import { FiltroPaisService } from '../../core/services/filtro-pais.service';
import { SeoService } from '../../core/services/seo.service';
import { detectDefaultPais, PaisCatalogo } from '../../core/models/catalogos';
import { Categoria, PaginatedResponse, Vacante, VacanteFiltros } from '../../core/models/vacante.model';
import { slugify } from '../../core/util/slugify';
import {
  construirPathVacantes,
  construirQueryParamsVacantes,
  resolverFiltrosANavegacion,
} from '../../core/util/vacante-search-nav';
import { JobCardComponent } from '../../shared/components/job-card/job-card.component';
import { BreadcrumbsComponent } from '../../shared/components/breadcrumbs/breadcrumbs.component';

@Component({
  selector: 'app-vacantes',
  standalone: true,
  imports: [CommonModule, FormsModule, JobCardComponent, BreadcrumbsComponent],
  templateUrl: './vacantes.component.html',
})
export class VacantesComponent implements OnInit {
  private vacantesService = inject(VacantesService);
  private categoriaService = inject(CategoriaService);
  private filtroPaisService = inject(FiltroPaisService);
  private seo = inject(SeoService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  categorias: Categoria[] = [];
  paises: PaisCatalogo[] = [];
  crumbs = [{ label: 'Inicio', url: '/' }, { label: 'Vacantes' }];

  sugerenciasUbicacion = signal<SugerenciaUbicacion[]>([]);
  /** true cuando el texto del campo no coincide con ninguna ciudad del catálogo. */
  sinCoincidenciasCiudad = signal(false);

  /**
   * Texto del campo Ciudad — lo que el usuario escribe o eligió. Solo al elegir
   * una sugerencia del catálogo pasa a ser el filtro aplicado (filtros.ciudad):
   * el texto libre sin coincidencia nunca filtra el listado ni entra a la URL.
   */
  ciudadTexto = '';

  /** true cuando el texto del campo es un borrador del usuario que no corresponde a una ciudad aplicada. */
  private ciudadEsBorrador = false;

  /** true cuando la ciudad aplicada ya se desaplicó en la edición actual — las teclas siguientes solo actualizan el borrador y las sugerencias, sin volver a navegar. */
  private ciudadDesaplicada = false;

  /** Nombre real de cada ciudad elegida del catálogo, por slug — permite restaurar el nombre ("Ciudad de México") tras back/forward en vez del slug crudo del path. */
  private ciudadesSeleccionadas = new Map<string, string>();

  private ciudadSearchSubject = new Subject<string>();
  private ocultarSugerenciasTimeout?: ReturnType<typeof setTimeout>;
  /** Si el campo Ciudad está enfocado — evita que una respuesta tardía reabra el dropdown tras el blur. */
  private ciudadEnfocada = false;

  /** true cuando el catálogo de países ya llegó y el país default está resuelto. */
  private paisesCargados = false;

  /** Primera búsqueda diferida hasta resolver el país default (ver ngOnInit). */
  private busquedaPendiente = false;

  resultado: PaginatedResponse<Vacante> = {
    items: [],
    page: 0,
    size: 12,
    totalItems: 0,
    totalPages: 0,
  };

  // true desde el inicio: la primera búsqueda queda diferida hasta resolver
  // el país default y el skeleton evita mostrar "no hay vacantes" antes.
  loading = true;

  // Slugs tal como vienen de la URL (fuente de la verdad para construir el
  // path); sector/pais numérico/código son solo para los <select> del UI.
  private categoriaSlug?: string;
  private paisSlug?: string;
  private ciudadSlug?: string;

  filtros: VacanteFiltros = {
    q: '',
    sector: undefined,
    ciudad: '',
    pais: '',
    tipoContrato: undefined,
    modalidad: undefined,
    sortBy: 'relevancia',
    page: 0,
    size: 12,
  };

  private searchSubject = new Subject<string>();
  private salaryFilterSubject = new Subject<void>();

  /** Secuencia de búsquedas — descarta respuestas de requests ya superados. */
  private buscarSecuencia = 0;

  ngOnInit(): void {
    this.vacantesService.obtenerPaises().subscribe((p) => {
      this.paises = p;
      this.paisesCargados = true;
      this.sincronizarSelectsConSlugs();
      // La primera búsqueda quedó diferida (ver queryParamMap): se ejecuta
      // ahora con el país default ya resuelto, para que el selector y el
      // request a /public/vacantes correspondan desde la primera carga.
      if (this.busquedaPendiente) {
        this.busquedaPendiente = false;
        this.buscar();
        this.actualizarSeo();
      }
    });

    this.categoriaService.listar().subscribe((cats) => {
      this.categorias = cats;
      // Las categorías llegan async — si la URL trae un slug de categoría,
      // hay que re-resolverlo contra el catálogo real antes de mostrar SEO.
      this.actualizarSeo();
    });

    this.route.paramMap.subscribe((params) => {
      const categoriaSlug = params.get('categoriaSlug') ?? undefined;
      const paisSlug = params.get('paisSlug') ?? undefined;
      const ciudadSlug = params.get('ciudadSlug') ?? undefined;
      const cambiaronSlugs =
        categoriaSlug !== this.categoriaSlug ||
        paisSlug !== this.paisSlug ||
        ciudadSlug !== this.ciudadSlug;
      this.categoriaSlug = categoriaSlug;
      this.paisSlug = paisSlug;
      this.ciudadSlug = ciudadSlug;
      this.sincronizarSelectsConSlugs();
      // Los cambios de path (país/ciudad/categoría) también deben refrescar el
      // listado: queryParamMap NO re-emite cuando la URL cambia solo de path
      // (los query params quedan idénticos), y cuando sí cambian, su emisión
      // llega ANTES que paramMap con los slugs todavía viejos — esta búsqueda
      // es la que corre con el estado definitivo (ver guard en buscar()).
      if (cambiaronSlugs) {
        this.buscarODiferir();
        this.actualizarSeo();
      }
    });

    this.route.queryParamMap.subscribe((params) => {
      this.redirigirFormatoLegado(params);

      this.filtros = {
        ...this.filtros,
        q: params.get('q') ?? '',
        tipoContrato: (params.get('tipoContrato') as VacanteFiltros['tipoContrato']) ?? undefined,
        seniority: (params.get('seniority') as VacanteFiltros['seniority']) ?? undefined,
        salarioMin: params.get('salarioMin') ? Number(params.get('salarioMin')) : undefined,
        salarioMax: params.get('salarioMax') ? Number(params.get('salarioMax')) : undefined,
        modalidad: (params.get('modalidad') as VacanteFiltros['modalidad']) ?? undefined,
        sortBy: (params.get('sortBy') as VacanteFiltros['sortBy']) ?? 'relevancia',
        page: params.get('page') ? Number(params.get('page')) : 0,
        size: 12,
      };
      this.buscarODiferir();
      this.actualizarSeo();
    });

    this.searchSubject.pipe(debounceTime(400), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe((q) => {
      this.updateQueryParams({ q, page: 0 });
    });

    this.salaryFilterSubject.pipe(debounceTime(600), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.applySalaryFilter();
    });

    // Sin distinctUntilChanged: si el listado se ocultó (blur) y el campo se
    // vuelve a enfocar con el mismo texto, el subject debe volver a emitir
    // para reabrir el dropdown (WEB-01).
    this.ciudadSearchSubject.pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef)).subscribe((q) => {
      // "Todos los países" (pais vacío) se respeta tal cual: NO se sustituye
      // por el país detectado del navegador (WEB-01).
      const paisBuscado = this.filtros.pais ?? '';
      this.vacantesService.ubicaciones(paisBuscado, q).subscribe((sugerencias) => {
        // Si el país o el texto cambiaron mientras la respuesta estaba en vuelo,
        // las sugerencias ya no corresponden y se descartan para no reinsertar
        // combinaciones país-ciudad inválidas (WEB-01).
        const paisActual = this.filtros.pais ?? '';
        if (paisActual !== paisBuscado || this.ciudadTexto !== q) return;
        // El texto sin coincidencias se señala aunque el campo ya haya perdido
        // el foco; el dropdown en sí solo se reabre si sigue enfocado, para que
        // una respuesta tardía no lo resucite después del blur.
        this.sinCoincidenciasCiudad.set(q.trim() !== '' && sugerencias.length === 0);
        if (!this.ciudadEnfocada) return;
        this.sugerenciasUbicacion.set(sugerencias);
      });
    });
  }

  /** Compatibilidad con enlaces viejos tipo /vacantes?sector=11 → redirige a la URL amigable. */
  private redirigirFormatoLegado(params: import('@angular/router').ParamMap): void {
    const legadoSector = params.get('sector');
    const legadoPais = params.get('pais');
    const legadoCiudad = params.get('ciudad');
    if (!legadoSector && !legadoPais && !legadoCiudad) return;

    const categoria = legadoSector
      ? this.categorias.find((c) => c.id === Number(legadoSector))
      : undefined;
    const pais = legadoPais ? this.paises.find((p) => p.code === legadoPais) : undefined;

    const path = this.construirPath({
      categoriaSlug: categoria?.slug,
      paisSlug: pais?.slug,
      ciudadSlug: legadoCiudad ? slugify(legadoCiudad) : undefined,
    });
    const resto: Record<string, string> = {};
    params.keys.forEach((k) => {
      if (k !== 'sector' && k !== 'pais' && k !== 'ciudad') {
        const value = params.get(k);
        if (value !== null) resto[k] = value;
      }
    });
    this.router.navigate(path, { queryParams: resto, replaceUrl: true });
  }

  private sincronizarSelectsConSlugs(): void {
    const categoria = this.categoriaSlug
      ? this.categorias.find((c) => c.slug === this.categoriaSlug)
      : undefined;

    // El país explícito del path es una elección concreta: se registra en el
    // servicio para que un "Todos los países" previo no persista al llegar,
    // p.ej., a /vacantes/pais/mexico desde un enlace (WEB-01).
    let pais: PaisCatalogo | undefined;
    if (this.paisSlug) {
      pais = this.paises.find((p) => p.slug === this.paisSlug);
      if (pais) this.filtroPaisService.registrar(pais.code);
    } else if (!this.filtroPaisService.todosLosPaises() && !this.ciudadSlug) {
      // Si la URL no trae país explícito, se usa el país detectado del
      // navegador como default — salvo que el usuario haya elegido "Todos
      // los países" en el select (esa elección se respeta) o la URL traiga
      // ciudad sin país (la ciudad puede pertenecer a otro país distinto al
      // detectado): en esos casos no se reinyecta ningún país (WEB-01).
      pais = detectDefaultPais(this.paises);
    }

    this.filtros = {
      ...this.filtros,
      sector: categoria?.id,
      pais: pais?.code ?? '',
      ciudad: this.ciudadSlug ?? '',
    };
    // El texto del campo debe corresponder a la ciudad de la URL: si la URL trae
    // ciudad y el texto ya no le corresponde (p.ej. back/forward sobre un
    // borrador), se restaura el nombre con el que se eligió la sugerencia; si
    // corresponde, se conserva el nombre real ("Bogotá") en vez de quedar con
    // el slug del path ("bogota"). Si la URL no trae ciudad, el texto solo se
    // conserva cuando es un borrador propio del usuario — el texto de una
    // ciudad ya no aplicada se limpia para no mostrar una ciudad distinta a la
    // URL actual.
    if (this.ciudadSlug) {
      if (slugify(this.ciudadTexto) !== this.ciudadSlug) {
        this.ciudadTexto = this.ciudadesSeleccionadas.get(this.ciudadSlug) ?? this.ciudadSlug;
      }
      this.ciudadEsBorrador = false;
      // La URL vuelve a tener una ciudad aplicada (p.ej. back/forward): el guard
      // de "desaplicar una sola vez" se re-arma para la próxima edición.
      this.ciudadDesaplicada = false;
    } else if (!this.ciudadEsBorrador) {
      this.ciudadTexto = '';
    }
  }

  /**
   * Busca con los filtros actuales; si el catálogo de países aún no llegó,
   * difiere la búsqueda: ejecutarla sin país devolvería vacantes de todos
   * los países mientras el selector muestra el país detectado.
   */
  private buscarODiferir(): void {
    if (this.paisesCargados) {
      this.buscar();
    } else {
      this.busquedaPendiente = true;
    }
  }

  buscar(): void {
    this.loading = true;
    // Guard contra respuestas fuera de orden: una navegación que cambia path
    // y query dispara dos búsquedas (queryParamMap primero, con slugs viejos,
    // y paramMap después, con los definitivos) — solo la última puede
    // escribir el listado.
    const secuencia = ++this.buscarSecuencia;
    this.vacantesService
      .buscar({
        ...this.filtros,
        categoriaSlug: this.categoriaSlug,
        paisSlug: this.paisSlug,
        ciudadSlug: this.ciudadSlug,
      })
      .subscribe((res) => {
        if (secuencia !== this.buscarSecuencia) return;
        this.resultado = res;
        this.loading = false;
      });
  }

  onSearchInput(value: string): void {
    this.searchSubject.next(value);
  }

  onCiudadInput(value: string): void {
    // El texto queda como borrador del campo; el filtro aplicado solo cambia
    // al elegir una sugerencia (elegirUbicacion) — un texto sin coincidencia
    // no debe filtrar el listado ni aparecer como ciudad en la URL.
    this.ciudadTexto = value;
    this.ciudadEsBorrador = true;
    this.sinCoincidenciasCiudad.set(false);
    // Si había una ciudad aplicada y el texto ya no le corresponde, la
    // selección deja de estar aplicada: texto visible y filtro/URL no pueden
    // quedar desincronizados (editar "colombia" sobre "Ciudad de México"
    // conservaba a esta última como filtro aplicado).
    const ciudadAplicada = this.ciudadSlug ?? this.filtros.ciudad;
    // ciudadSlug conserva el slug anterior hasta que paramMap refleje la
    // navegación: sin este guard, cada tecla volvería a desaplicar y navegar.
    if (ciudadAplicada && !this.ciudadDesaplicada) {
      const sigueCorrespondiendo = this.ciudadSlug
        ? slugify(value) === this.ciudadSlug
        : value === this.filtros.ciudad;
      if (!sigueCorrespondiendo) {
        this.ciudadDesaplicada = true;
        this.filtros.ciudad = '';
        this.onFilterChange();
      }
    }
    this.ciudadSearchSubject.next(value);
  }

  onCiudadFocus(): void {
    this.ciudadEnfocada = true;
    // Abrir el listado también con el campo vacío: la consulta se filtra por
    // el país actual, o por ninguno si está seleccionado "Todos los países".
    // Antes solo se buscaba si ya había texto, por lo que tras seleccionar
    // un país (que limpia la ciudad) el dropdown nunca volvía a abrirse.
    this.ciudadSearchSubject.next(this.ciudadTexto || '');
  }

  /** Delay para que el (click) de una sugerencia alcance a disparar antes de que el blur la oculte. */
  ocultarSugerenciasConDelay(): void {
    this.ciudadEnfocada = false;
    this.ocultarSugerenciasTimeout = setTimeout(() => this.sugerenciasUbicacion.set([]), 150);
  }

  elegirUbicacion(s: SugerenciaUbicacion): void {
    if (this.ocultarSugerenciasTimeout) clearTimeout(this.ocultarSugerenciasTimeout);
    // Se aplica la ciudad del catálogo, no la etiqueta completa: la etiqueta
    // puede incluir distrito ("Álvaro Obregón, Ciudad de México") y su slug no
    // corresponde al ciudadSlug de ninguna vacante (el backend filtra exacto),
    // dejando el listado en 0 tras la selección.
    const ciudad = s.ciudad || s.etiqueta;
    this.ciudadTexto = ciudad;
    this.ciudadEsBorrador = false;
    this.ciudadDesaplicada = false;
    this.filtros.ciudad = ciudad;
    // Se recuerda el nombre real por slug: si el usuario navega back/forward
    // a esta URL, el campo vuelve a mostrar el nombre y no el slug crudo.
    this.ciudadesSeleccionadas.set(slugify(ciudad), ciudad);
    this.sinCoincidenciasCiudad.set(false);
    this.sugerenciasUbicacion.set([]);
    this.onFilterChange();
  }

  /**
   * Al cambiar de país, la ciudad seleccionada puede pertenecer a otro país
   * (ej. Bogotá bajo México) — se limpia para no navegar combinaciones
   * incorrectas como /vacantes/pais/mexico/ciudad/bogota. Las sugerencias se
   * limpian siempre (aunque la ciudad esté vacía): el dropdown pudo quedar
   * abierto con sugerencias del país anterior.
   *
   * "Todos los países" (pais vacío) queda registrado en el servicio para que
   * la sincronización con la URL (también en otras vistas, como Home) no
   * reinyecte el país detectado del navegador (WEB-01).
   */
  onPaisChange(pais: string): void {
    this.filtroPaisService.registrar(pais);
    this.filtros.pais = pais;
    this.filtros.ciudad = '';
    this.ciudadTexto = '';
    this.ciudadEsBorrador = false;
    this.ciudadDesaplicada = false;
    this.sinCoincidenciasCiudad.set(false);
    this.sugerenciasUbicacion.set([]);
    this.onFilterChange();
  }

  onFilterChange(): void {
    const { path, queryParams } = resolverFiltrosANavegacion(this.filtros, this.categorias, this.paises);
    // URL destino construida antes de navegar: si es la misma que la actual,
    // la ruta no re-emite (p.ej. elegir "Todos los países" desde una URL sin
    // país) y el listado se refresca aquí directo para que los resultados
    // sigan correspondiendo a los filtros.
    const destino = this.router.createUrlTree(path, { queryParams }).toString();
    if (destino === this.router.url) {
      this.buscarODiferir();
      return;
    }
    this.router.navigate(path, { queryParams });
  }

  onSalaryFilterChange(): void {
    this.salaryFilterSubject.next();
  }

  private applySalaryFilter(): void {
    const scrollY = typeof window !== 'undefined' ? window.scrollY : 0;
    const { path, queryParams } = resolverFiltrosANavegacion(this.filtros, this.categorias, this.paises);
    this.router.navigate(path, { queryParams }).then((success) => {
      if (success && scrollY > 0) {
        // Defer past Angular's own scroll-position-restoration handler
        setTimeout(() => window.scrollTo({ top: scrollY }), 0);
      }
    });
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.resultado.totalPages) return;
    this.updateQueryParams({ page });
  }

  private construirPath(slugs: { categoriaSlug?: string; paisSlug?: string; ciudadSlug?: string }): string[] {
    return construirPathVacantes(slugs);
  }

  private updateQueryParams(extra: Partial<VacanteFiltros>): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.queryParamsSecundarios({ ...this.filtros, ...extra }),
      queryParamsHandling: '',
    });
  }

  // Solo q/tipoContrato/seniority/salarioMin/salarioMax/modalidad/sortBy/page viajan
  // como query params — categoría, país y ciudad viven en el path (ver construirPath).
  private queryParamsSecundarios(filtros: Partial<VacanteFiltros>): Record<string, string> {
    return construirQueryParamsVacantes(filtros);
  }

  private actualizarSeo(): void {
    const parts: string[] = [];
    const categoria = this.categorias.find((c) => c.slug === this.categoriaSlug);
    const pais = this.paisSlug ? this.paises.find((p) => p.slug === this.paisSlug) : undefined;
    if (categoria) parts.push(categoria.nombre);
    if (this.ciudadSlug) parts.push(`en ${this.filtros.ciudad}`);
    if (pais) parts.push(pais.nombre);

    const suffix = parts.length ? ` — ${parts.join(' ')}` : '';
    const title = `Vacantes de empleo${suffix} | Chambia`;
    const description = parts.length
      ? `Encuentra empleos de ${parts.join(' ')} en Chambia. Postula gratis por WhatsApp con matching por IA.`
      : 'Explora miles de vacantes en LATAM: construcción, ventas, tecnología, salud y más. Postula gratis por WhatsApp.';

    this.seo.setMetaTags({
      title,
      description,
      path: this.router.url.split('?')[0],
    });
  }
}
